import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { settingsApi } from '../../api/settings_api';
import type { EmailAddress } from '../../types';

// The account's email addresses (/settings/edit). Changes save immediately,
// separately from the profile form's Save button.
export function EmailAddresses() {
  const [items, setItems] = useState<EmailAddress[] | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    settingsApi
      .listEmails()
      .then((r) => setItems(r.items))
      .catch((err) => setError(err instanceof Error ? err.message : 'Unable to load email addresses'));
  }, []);

  const run = async (key: string, action: () => Promise<{ items: EmailAddress[] }>, done?: string) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const r = await action();
      setItems(r.items);
      if (done) setNotice(done);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;
    const ok = await run('add', () => settingsApi.addEmail(email),
      `We sent a confirmation link to ${email}. It will be added once the link is clicked.`);
    if (ok) setNewEmail('');
  };

  if (!items) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Addresses
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Requests emailed from any confirmed address are added to this account.
        Notifications go to the primary address. Any confirmed address can be used to sign in.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <List dense disablePadding>
        {items.map((item) => (
          <ListItem
            key={item.email}
            divider
            disableGutters
            secondaryAction={
              !item.is_primary && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {item.status === 'verified' ? (
                    <Button
                      size="small"
                      disabled={busy !== null}
                      onClick={() => run(`primary:${item.email}`, () => settingsApi.setPrimaryEmail(item.email),
                        `${item.email} is now the primary address.`)}
                    >
                      Make Primary
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      disabled={busy !== null}
                      onClick={() => run(`resend:${item.email}`, () => settingsApi.addEmail(item.email),
                        `We sent a new confirmation link to ${item.email}.`)}
                    >
                      Resend Link
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    disabled={busy !== null}
                    onClick={() => run(`remove:${item.email}`, () => settingsApi.removeEmail(item.email))}
                  >
                    Remove
                  </Button>
                </Box>
              )
            }
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 24 }}>
                  <Typography variant="body1" component="span" sx={{ wordBreak: 'break-all' }}>
                    {item.email}
                  </Typography>
                  {item.is_primary && <Chip label="Primary" color="primary" size="small" />}
                  {item.status === 'pending' && (
                    <Chip
                      label={item.expired ? 'Link expired' : 'Pending confirmation'}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start' }}>
        <TextField
          label="Add an email address"
          type="email"
          size="small"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          disabled={busy !== null}
          sx={{ flex: 1 }}
        />
        <Button type="submit" variant="outlined" disabled={busy !== null || !newEmail.trim()}>
          {busy === 'add' ? <CircularProgress size={20} color="inherit" /> : 'Add'}
        </Button>
      </Box>
    </Box>
  );
}
